export interface Fee {
  label: string
  value: number | string
}

export interface PlanInfo {
  advanceMonth: number
  vas: string
  bom: string
  fees: Fee[]
  mdrDetails: MdrDetails[]
}

export interface MDRResponse {
  getMDRFee: MdrDetails[]
}

export interface MdrDetails {
  mdrId: string
  vasName: string
  name: string
  cardType: string
  rateType: string
  fixedValue: string
  slabValueDetails: SlabValueDetail[]
  slabList?: SlabValueDetail[]
  slabValMDR: boolean
  feeTypeLabel: string
  value?: string
  id?: string
  editable: boolean
}

export interface SlabValueDetail {
  cardNetwork: string
  mdrType: string
  slabList: Slab[]
}

export interface Slab {
  lowerLimit: number
  rate: number
  rateType: number
  upperLimit: number
  annualTurnover: AnnualTurnover[]
}

export interface AnnualTurnover {
  capAmount: number
  lowerLimitAmount: number
  rate: number
  rateType: number
  upperLimitAmount: number
}

export interface Plan {
  planId: string
  planName: string
  quantity?: number
  standardProductWrapper?: PlanInfo
  relatedProductsWrapper?: PlanInfo[]
}

export interface POSProduct {
  id: number
  sfReferenceId: string
  name: string
  productImageUrl: string
  planSummary?: OLIDetail[]
}
export interface POSPlan {
  id: number
  name: string
  description?: string
  advanceMonth?: number
  quantity: number
}

export interface ProductWithPlans extends POSProduct {
  plans: Plan[]
  totalPlans?: number
  plansSummary?: string
}

export interface ProductList {
  productList: {
    products: POSProduct[]
    totalSize: number
  }
}

export interface SelectedProductList {
  getOpportunityProduct: {
    products: ProductWithPlans[]
  }
}

export interface PlanList {
  planList: {
    plans: Plan[]
  }
}

export interface VASTable {
  id: string
  vasName: string
  feeType: string
  instrumentName: string
  rateType: string
  value: string
  editable: boolean
}

export interface SlabTable {
  cardNetwork: string
  instrumentName: string
  rateType: string
  lowerLimit: number
  upperLimit: number
  lowerLimitAmount: number
  upperLimitAmount: number
  capAmount: number
  sellRate: number
  editable: boolean
}

export interface ProductMap {
  [id: string]: ProductWithPlans
}

export type PlanMap = Map<string, POSPlan>

export enum ProductSelectionFlow {
  SALESFORCE = 'SALESFORCE',
  IN_APP = 'IN_APP',
}

export enum ProductSelectionRoutes {
  'classic' = 'classic',
  'select-products' = 'select-products',
  'products-summary' = 'products-summary',
}

export enum PaymentType {
  FULLPAYMENT = 'FULLPAYMENT',
  PARTPAYMENT = 'PARTPAYMENT',
  POSTDEPLOYMENTPAYMENT = 'POSTDEPLOYMENTPAYMENT',
  POSTINVOICING = 'POSTINVOICING',
  SPONSOREDPAYMENT = 'SPONSOREDPAYMENT',
}

export interface AllOLIData {
  getAllOLIs: OLIDetail[]
}

export interface OLIDetail {
  id: number
  planId: number
  name: string
  productId: string
  accountId: string
  opportunityId: string
  priceType: string
  customAdvanceMonth: number
  standardAdvanceMonth: number
  standardPrice: number
  customPrice: number
  customPriceToDisplay: number
  quantity: number
  buyPrice: string
  unitPrice: number
  listPrice: number
  subTotal: number
  total: number
  acquirer: string
  bankAccountDetail: string
  cseL1Status: string
  cseL2Status: string
  riskStatus: string
  gstRate: number
  mcc: string
  unitOfMeasurement: string
  setupFee: string
  subscriptionFee: string
  nbsId: number
  type: string
  vasReferenceId: string
  posReferenceId: string
  feeReferenceId: string
  mdrFeeReferenceId: string
  edcItemNumber: string
  createdOn: string
  modifiedOn: string
  createdBy: number
  modifiedBy: string
  feeMappingReferenceId: string
  aggregator: boolean
}

export interface ProductPlanDetail {
  getProductsViewByPlan: PlanDetail[]
}
export interface MdrFee {
  id: number
  sfReferenceId: string
  name: string
  cardSegment: string | null
  cardSegmentId: number | null
  instrumentType: number
  slabList: string | null
  value: string
  issuer: string | null
  insurancePartner: string | null
  insuranceSubPartner: string | null
  vasReference: number
  insuranceIssuer: string | null
  insuranceProvider: string | null
  createdOn: string | null
  modifiedOn: string | null
  createdBy: string | null
  modifiedBy: string | null
  active: boolean | null
  mdrType?: string
}

export interface PlanDetail {
  planId: number
  id: number
  sfReferenceId: string
  name: string
  description: string
  isActive: boolean
  salesCategory: string
  mccList: string
  advanceMonth: number
  isAdvanceMonthEditable: boolean
  nbsId: number
  vasReferenceId: string
  fees: Fee[]
  posProduct: PosProduct
  vasProduct: VasProduct
  eligibleFor3PUser: boolean
  category: string
  businessSubCategory: string
  quantity?: number
  sortOrder: string
  isAggregator: boolean
  acquirerType: string
  isHostActivationRequired: boolean
  acquirer: string
  customAdvancedMonth?: number
  pricingType?: string
  aggregator: boolean
}

export interface Fee {
  id: number
  name: string
  description: string
  fee: string
  feeType: string
  unitOfMeasurement: string
  minValue: string
  maxValue: string
  msfType: string
  frequency: string
  downStreamNBSId: number
  entityType: string
  navFeeCode: string
  setupFee: string
  subscription: string
  isActive: string
}
export interface PosProduct {
  id: number
  sfReferenceId: string
  name: string
  fees: Fee[]
  posType: string
  hardwareModel: string
  downstreamPosId: string
  edcType: string
  edcItemNumber: string
  productImageUrl: string
  subTypeVersionId: number
  subTypeVersionName: string
  hardwareSubTypeId: number
  hardwareSubTypeName: string
  selectedQuantity: number
}

export interface VasProduct {
  id: string
  sfReferenceId: string
  name: string
  downstreamVasId: number
  aggregatorProgramId: number
  aggregatorProgramName: string
  isPG: boolean
  fees: Fee[]
  bankDetails: boolean
  settlementCycle: string
  modeOfPay: string
  acquirer: string
  selectedQuantity: number
  mdrfees?: MdrFee[]
}
